/**
 * The size box beside an arm control (one-click and Attack).
 *
 * The number in this box is what the next armed click or hotkey sends, so the
 * box never changes it on a keystroke. What the trader types is a local DRAFT;
 * it becomes the live size only on Enter or blur, and only when it is a whole
 * number from 1 to the trader's effective maximum. Everything else is refused
 * and the last good size stays live:
 *
 *   • empty, zero, fractional or unparsable — refused, never defaulted. The
 *     old box committed every keystroke, so backspacing to empty set the
 *     default (100) and the digits typed after it appended to "100" and
 *     clamped to the ceiling: the next armed press sent a 100–500 lot.
 *   • over the maximum — refused, never clamped. A clamp sends a size the
 *     trader did not type.
 *
 * While the trader types, the box shows the draft, red when it cannot be
 * committed, and a short line above the controls says which size is live and
 * what applies it. After every Enter and every blur the box shows the live
 * size: a refused draft reverts on the spot and the line says why, naming
 * the size that stands. Only a refused Enter plays the order-rejected tone
 * (alert preferences decide whether it is heard); a refused blur stays
 * silent, so a click that leaves the box to place an order at the live size
 * never rings "rejected" first. A committed Enter and Escape (which drops the
 * draft) both leave the box, so the next bound key reaches the hotkey
 * listener. The store, the ack strip and the readout all keep reading the
 * live size; nothing outside this box sees a draft.
 *
 * A draft lives only while the box can be typed in. When the box leaves the
 * screen (its grant slot hides) or turns disabled (its grant goes pending),
 * the draft is dropped, never applied: an unmount sends no blur, so a kept
 * draft would come back over a box whose live size it does not match, and the
 * blur a browser sends when it disables a focused box is not the trader
 * clicking away, so it must not make a half-typed size live.
 *
 * The line sits ABOVE the controls, absolutely positioned, so it never moves
 * them (#1070) and never covers the status box GameDetail holds under them.
 * The two boxes' lines take separate lanes, because both can show at once (a
 * new trader whose limits lower both sizes): the one-click line starts at
 * the one-click control's left edge, the Attack line at its own box.
 */

import { useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { cn } from '@/lib/infra/utils';
import { playLocalRefusalTone } from '@/stores/oneClickStore';

export type SizeDraftVerdict = { ok: true; size: number } | { ok: false; message: string };

/**
 * May this draft become the live size? Pure; exported for tests.
 *
 * `limitName` names whose maximum it is in the refusal ("one-click",
 * "Attack"), so the trader knows which setting bounds them.
 */
export function judgeSizeDraft(
  draft: string,
  live: number,
  max: number,
  limitName: string,
): SizeDraftVerdict {
  const text = draft.trim();
  if (/^\d+$/.test(text)) {
    const n = Number(text);
    if (n > max) return { ok: false, message: `Size unchanged (${live}): max ${max} (your ${limitName} limit)` };
    if (n >= 1) return { ok: true, size: n };
  }
  return { ok: false, message: `Size unchanged (${live}): enter 1–${max}` };
}

type NoticeTone = 'invalid' | 'pending' | 'lowered';

type Notice = { text: string; tone: NoticeTone };

export interface ArmedSize {
  /** What the box shows: the draft while the trader types, else the live size. */
  value: string;
  max: number;
  /** The draft cannot be committed (red border, aria-invalid). */
  invalid: boolean;
  notice: Notice | null;
  /** Ties the input to its notice (aria-describedby). */
  noticeId: string;
  onChange: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur: (event: FocusEvent<HTMLInputElement>) => void;
}

/**
 * The draft/commit state for one size box, plus the convergence the controls
 * already ran: a live size above the effective maximum (a new identity, or a
 * lowered limit) is set down to it — and that is now announced, because it
 * changes what the next order sends.
 *
 * Call it where the control called its convergence effect (before any early
 * return), so the size converges whether or not the box is on screen.
 * `active` says whether the box is on screen and enabled; when it turns
 * false the draft is dropped without being applied.
 */
export function useArmedSize({
  size,
  max,
  setSize,
  limitName,
  active,
}: {
  size: number;
  max: number;
  setSize: (n: number) => void;
  limitName: string;
  active: boolean;
}): ArmedSize {
  const noticeId = useId();
  const [draft, setDraftState] = useState<string | null>(null);
  // The draft as the handlers see it. Enter and Escape blur the box inside
  // their own handler, and that blur must see the draft they just applied or
  // dropped, not the one this render closed over.
  const draftRef = useRef<string | null>(null);
  const [loweredTo, setLoweredTo] = useState<number | null>(null);
  // What the last Enter, blur or Escape left to say, for the size it names.
  const [settled, setSettled] = useState<(Notice & { live: number }) | null>(null);

  useEffect(() => {
    if (size > max) {
      setSize(max);
      // The notice belongs to this convergence, which is an effect on the
      // external store; setting it anywhere else would announce a lowering
      // the store has not made yet. One extra render, only when it lowers.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoweredTo(max);
    }
  }, [max, setSize, size]);

  const setDraft = (next: string | null) => {
    draftRef.current = next;
    setDraftState(next);
  };

  useEffect(() => {
    if (!active) {
      // The box is gone or disabled: drop the draft, never apply it, so the
      // box shows the live size when it can be typed in again.
      draftRef.current = null;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDraftState(null);
    }
  }, [active]);

  const verdict = draft == null ? null : judgeSizeDraft(draft, size, max, limitName);
  const pending = verdict != null && !(verdict.ok && verdict.size === size);

  let notice: Notice | null = null;
  if (loweredTo != null && loweredTo === size) {
    // A size just lowered to this trader's limit says so until a size is
    // applied, over a draft too: never "unchanged" for a size that changed.
    notice = { text: `Size lowered to ${loweredTo} (this trader's limit)`, tone: 'lowered' };
  } else if (verdict != null && pending) {
    notice = verdict.ok
      ? { text: `Size unchanged (${size}): Enter or click away to apply ${verdict.size}`, tone: 'pending' }
      : { text: verdict.message, tone: 'invalid' };
  } else if (settled != null && settled.live === size) {
    notice = settled;
  }

  /**
   * Apply the draft, or refuse it and show the live size again. `ring`: an
   * Enter rings the refusal tone; a blur never does.
   */
  const commit = (ring: boolean): 'none' | 'applied' | 'refused' => {
    const current = draftRef.current;
    if (current == null) return 'none';
    const result = judgeSizeDraft(current, size, max, limitName);
    setDraft(null);
    if (result.ok) {
      if (result.size !== size) setSize(result.size);
      setSettled(null);
      setLoweredTo(null);
      return 'applied';
    }
    setSettled({ text: result.message, tone: 'invalid', live: size });
    if (ring) playLocalRefusalTone();
    return 'refused';
  };

  return {
    value: draft ?? String(size),
    max,
    invalid: verdict != null && !verdict.ok,
    notice,
    noticeId,
    onChange: (value) => {
      setDraft(value);
      setSettled(null);
    },
    onKeyDown: (event) => {
      const input = event.currentTarget;
      if (event.key === 'Enter') {
        event.preventDefault();
        if (commit(true) === 'refused') {
          // The live size, selected, so the retry replaces it rather than
          // appending to it.
          input.value = String(size);
          input.select();
          return;
        }
        input.blur();
      } else if (event.key === 'Escape') {
        setDraft(null);
        setSettled({ text: `Size unchanged (${size})`, tone: 'pending', live: size });
        input.blur();
      }
    },
    onBlur: (event) => {
      // A box disabled (or removed) while focused loses focus without the
      // trader leaving it: drop the draft rather than apply it.
      const input = event.currentTarget;
      if (input.disabled || !input.isConnected) {
        setDraft(null);
        return;
      }
      commit(false);
    },
  };
}

const NOTICE_TONE: Record<NoticeTone, string> = {
  invalid: 'text-negative',
  pending: 'text-muted-foreground',
  lowered: 'text-amber-400',
};

/**
 * The line above the controls. Renders nothing when there is nothing to say;
 * its host must be `relative`, and the line is out of flow so nothing moves.
 */
export function ArmedSizeNotice({ box, className }: { box: ArmedSize; className?: string }) {
  if (!box.notice) return null;
  return (
    <span
      id={box.noticeId}
      role="status"
      data-testid="armed-size-notice"
      className={cn(
        'bg-background pointer-events-none absolute bottom-full left-0 z-10 whitespace-nowrap px-0.5 font-sans text-[11px] leading-tight',
        NOTICE_TONE[box.notice.tone],
        className,
      )}
    >
      {box.notice.text}
    </span>
  );
}

export function ArmedSizeField({
  box,
  label,
  quickRef,
  disabled,
  emphasized,
  widthPad,
  noticeOnBox = true,
}: {
  box: ArmedSize;
  /** Accessible name of the input. */
  label: string;
  /** The quick reference, as the box's native hover title. Never a focus
   *  tooltip: one opened under the box covered the status strip. */
  quickRef: string;
  disabled: boolean;
  /** Armed: the size reads in the foreground colour. */
  emphasized: boolean;
  /** Horizontal room for the spinner and padding beside the digits. */
  widthPad: string;
  /** False when the host places {@link ArmedSizeNotice} itself. */
  noticeOnBox?: boolean;
}) {
  return (
    <label
      title={quickRef}
      className={cn(
        'relative flex shrink-0 items-center gap-1 font-mono text-xs',
        emphasized ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <span>×</span>
      <input
        type="number"
        min={1}
        max={box.max}
        aria-label={label}
        aria-invalid={box.invalid || undefined}
        aria-describedby={box.notice ? box.noticeId : undefined}
        disabled={disabled}
        value={box.value}
        onChange={(e) => box.onChange(e.target.value)}
        onKeyDown={box.onKeyDown}
        onBlur={box.onBlur}
        // #1070: sized to the largest value this trader may enter (the
        // ceiling), not the current value — the box never resizes as digits
        // are typed or a clamp lands, and never clips an allowed size.
        style={{ width: `calc(${Math.max(3, String(box.max).length)}ch + ${widthPad})` }}
        className={cn(
          'bg-background rounded border px-1.5 py-0.5 text-right tabular-nums',
          box.invalid ? 'border-negative' : 'border-border/50',
        )}
      />
      {/* mb-0.5: level with the arm buttons' top edge, which sit 2px
          higher than the box. */}
      {noticeOnBox && <ArmedSizeNotice box={box} className="mb-0.5" />}
    </label>
  );
}
